import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { defaultPersistentForAlertType } from "@/lib/notification-methods";
import { teamConversationIdFrom } from "@/lib/notification-reference";
import { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { NotificationMethod } from "@/types/enums/notification-method";
import { NotificationAlertSummary, NotificationCursorPage, NotificationFetchParams, NotificationPreference, NotificationPreset, ProviderNotificationSummary } from "@/types/notification";
import { applyNotificationPreset, createNotificationPreferences, deleteNotificationPreference, getNotificationPreferences, getNotificationPresets } from "@/api/provider/admin/settings";
import { BulkNotificationAction, archiveNotifications, bulkNotificationAction, fetchCombinedNotifications, fetchNotifications, markNotificationsRead, markNotificationsUnread } from "@/api/provider/notifications/notifications";

// Type for infinite query data with cursor-based pagination
type NotificationInfiniteData = InfiniteData<NotificationCursorPage | null, string | null>;

// Query keys
export const notificationQueryKeys = {
  all: ["notifications"] as const,
  list: (filters: Omit<NotificationFetchParams, "cursor" | "perPage">) => [...notificationQueryKeys.all, "list", filters] as const,
  preferences: () => [...notificationQueryKeys.all, "preferences"] as const,
  combined: (notificationId: string) => [...notificationQueryKeys.all, "combined", notificationId] as const,
  availableAlertTypes: () => [...notificationQueryKeys.all, "available-alert-types"] as const,
  presets: () => [...notificationQueryKeys.all, "presets"] as const
};

// Query options
const notificationQueryOptions = {
  preferences: () => ({
    queryKey: notificationQueryKeys.preferences(),
    queryFn: async (): Promise<NotificationPreference[]> => {
      const data = await getNotificationPreferences();
      // The same response says which types are still delivered; keep it so
      // useAvailableAlertTypes needs no request of its own.
      queryClient.setQueryData<ReadonlySet<AlertType>>(notificationQueryKeys.availableAlertTypes(), new Set(data?.availableAlertTypes ?? []));
      return data?.preferences ?? [];
    },
    staleTime: 1000 * 60 * 5 // 5 minutes - preferences change infrequently
  }),
  infiniteList: (filters: Omit<NotificationFetchParams, "cursor" | "perPage"> = {}, perPage: number = 10) => ({
    queryKey: notificationQueryKeys.list(filters),
    queryFn: ({
      pageParam = null
    }: {
      pageParam?: string | null;
    }) => fetchNotifications({
      ...filters,
      cursor: pageParam,
      perPage
    }),
    getNextPageParam: (lastPage: NotificationCursorPage | null) => lastPage?.nextCursor ?? undefined,
    initialPageParam: null
  })
};

// Optimistic inserts can overlap a later cursor response. Keep server cursors
// intact, but expose each row only once in global chronological order. An
// optimistic older row in page zero must not precede newer rows in later pages.
const selectUniqueNotifications = (data: NotificationInfiniteData) => {
  const seenIds = new Set<string>();
  const uniquePages = data.pages.map(page => page ? {
    ...page,
    data: page.data.filter(row => {
      if (seenIds.has(row.id)) return false;
      seenIds.add(row.id);
      return true;
    })
  } : page);
  const sortedRows = uniquePages.flatMap(page => page?.data ?? []).sort((a, b) => {
    const dateOrder = new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
    // Match the server's descending ID tie-break for equal timestamps.
    return dateOrder || (a.id === b.id ? 0 : a.id < b.id ? 1 : -1);
  });
  let offset = 0;
  return {
    ...data,
    pages: uniquePages.map(page => {
      if (!page) return page;
      const rows = sortedRows.slice(offset, offset + page.data.length);
      offset += page.data.length;
      return {
        ...page,
        data: rows
      };
    })
  };
};

// Hooks
/** What a stacked row expands to. Only fetched once the row is opened. */
const combinedNotificationsQuery = (notificationId: string) => ({
  queryKey: notificationQueryKeys.combined(notificationId),
  queryFn: async () => (await fetchCombinedNotifications(notificationId)) ?? []
});
export const useCombinedNotifications = (notificationId, enabled) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== notificationId) {
    t0 = combinedNotificationsQuery(notificationId);
    $[0] = notificationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== enabled || $[3] !== t0) {
    t1 = {
      ...t0,
      enabled
    };
    $[2] = enabled;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
};

/** The rows combined into this one, fetched fresh: alerts may have joined since an expand cached them. */
export const getCombinedNotifications = (notificationId: string) => queryClient.fetchQuery({
  ...combinedNotificationsQuery(notificationId),
  staleTime: 0
});

/**
 * The alert types the server still delivers. Retired types are left out, so a list
 * built from ALERT_SECTIONS can drop switches that would save and do nothing.
 */
export const useAvailableAlertTypes = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: notificationQueryKeys.availableAlertTypes(),
      queryFn: _temp,
      staleTime: Infinity
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
};

/** The None/Low/Medium/High levels for this user, with this clinic's daily estimate. */
export const useNotificationPresets = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: notificationQueryKeys.presets(),
      queryFn: getNotificationPresets
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
};

/** Every write to the preference rows, so picking a level can wait for them to settle. */
export const PREFERENCE_WRITE_MUTATION_KEY = [...notificationQueryKeys.all, "preference-write"] as const;

/** Lets the settings page hold its other writes while a level is being applied. */
export const APPLY_NOTIFICATION_PRESET_MUTATION_KEY = [...notificationQueryKeys.all, "apply-preset"] as const;

/** Switch to one level. The server replaces the in-app rows wholesale, so take its list. */
export const useApplyNotificationPresetMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: APPLY_NOTIFICATION_PRESET_MUTATION_KEY,
      mutationFn: _temp2,
      onSuccess: _temp3,
      onSettled: _temp4
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useNotificationPreferences = t0 => {
  const $ = _c(5);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = notificationQueryOptions.preferences();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== options.enabled) {
    t3 = {
      ...t2,
      enabled: options.enabled
    };
    $[3] = options.enabled;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return useQuery(t3);
};
export const useNotificationsInfinite = (t0, t1) => {
  const $ = _c(7);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const filters = t2;
  const perPage = t1 === undefined ? 10 : t1;
  let t3;
  if ($[2] !== filters || $[3] !== perPage) {
    t3 = notificationQueryOptions.infiniteList(filters, perPage);
    $[2] = filters;
    $[3] = perPage;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = {
      ...t3,
      select: selectUniqueNotifications
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useInfiniteQuery(t4);
};

/** Page size shared by the panel and unread count hook. */
export const NOTIFICATIONS_PER_PAGE = 20;

/**
 * Hook to get unread notification count from the server response.
 * Shares a cache entry with the panel's usual unread default. Auto-mark-on-open
 * providers use All in the panel so newly read rows remain visible.
 */
export const useUnreadNotificationCount = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      read: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data
  } = useNotificationsInfinite(t0, NOTIFICATIONS_PER_PAGE);
  return data?.pages?.[0]?.unreadCount ?? 0;
};

// Invalidation helpers
export const invalidateNotificationList = () => {
  // Invalidate only the list queries
  return queryClient.invalidateQueries({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
};

/** Whether `row` is the unread head of the stack `incoming` has just replaced. */
const supersededBy = (row: ProviderNotificationSummary, incoming: ProviderNotificationSummary) => !!incoming.groupId && row.groupId === incoming.groupId && !row.read && row.id !== incoming.id;

/** Whether `a` sorts after `b` the way the server picks a stack's newest row. */
const isNewerThan = (a: ProviderNotificationSummary, b: ProviderNotificationSummary) => {
  const aTime = Date.parse(a.createdDate);
  const bTime = Date.parse(b.createdDate);
  return aTime !== bTime ? aTime > bTime : a.id > b.id;
};

/**
 * Stacks this tab has already put on the bell. A stack is one notification on the
 * badge, but its frames can arrive in any order and the first one can arrive
 * already hidden (`stacked`), so the count is keyed on the stack, not on
 * `messageCount`.
 */
const countedStacks = new Set<string>();

/** Test hook: forget which stacks were counted. */
export const resetCountedStacks = () => countedStacks.clear();

/**
 * Whether an unread live frame adds one to the badge: a lone row always does, a
 * stack only on its first frame here, and not when a loaded list already shows
 * the stack unread (the server's count includes it).
 */
const addsToBadge = (notification: ProviderNotificationSummary, lists: [readonly unknown[], NotificationInfiniteData | undefined][]) => {
  if (notification.read) return false;
  const groupId = notification.groupId;
  if (!groupId) return true;
  if (countedStacks.has(groupId)) return false;
  countedStacks.add(groupId);
  return !lists.some(([, data]) => data?.pages?.some(page => page?.data.some(n => n.groupId === groupId && !n.read)));
};

/**
 * Put a live notification into the cached lists. Returns false when the frame is
 * stale -- an older row of a stack the cache already shows newer -- so the caller
 * can skip its toast too.
 */
export const appendNotificationToCache = (alert: AlertDetail): boolean => {
  if (!alert.notification) return false;

  // Check if any notification list queries have cached data.
  // If not (panel never opened), invalidate so the next fetch picks up
  // the new notification instead of silently dropping it.
  const existingQueries = queryClient.getQueriesData<NotificationInfiniteData>({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  const notification = alert.notification;

  // Usually born unread — but not always. A social DM whose thread was opened
  // before the background task inserted the row arrives already read (see
  // create_notifications_for_alert), and must land in the read-filtered caches
  // without bumping the unread count.
  const isRead = alert.notification.read;

  /** Whether a list cache's read filter admits a notification in this state. */
  const acceptsNotification = (queryKey: readonly unknown[]) => {
    const filters = queryKey.length >= 3 && typeof queryKey[2] === "object" ? queryKey[2] as {
      read?: boolean | null;
    } : undefined;
    return !filters || filters.read == null || filters.read === isRead;
  };

  // A row the server hid in its stack on arrival (an alert that lost the race to
  // be newest) carries only the stack's new count: bring the shown row up to
  // date, count the stack if this is the first this tab has heard of it, and
  // raise nothing.
  if (notification.stacked) {
    const bump = addsToBadge(notification, existingQueries) ? 1 : 0;
    for (const [queryKey] of existingQueries) {
      const counts = acceptsNotification(queryKey);
      queryClient.setQueryData<NotificationInfiniteData>(queryKey, oldData => oldData && {
        ...oldData,
        pages: oldData.pages.map((page, index) => page && {
          ...page,
          unreadCount: counts && index === 0 && page.unreadCount != null ? page.unreadCount + bump : page.unreadCount,
          data: page.data.map(n => supersededBy(n, notification) ? {
            ...n,
            messageCount: Math.max(n.messageCount ?? 1, notification.messageCount ?? 1)
          } : n)
        })
      });
    }
    return false;
  }

  // Two alerts for one stack can be delivered out of order. If any cached list
  // already shows a newer row of this stack, the server has hidden this one --
  // checked across every list before touching any, so none inserts it.
  const stale = existingQueries.some(([, data]) => data?.pages?.some(page => page?.data.some(n => supersededBy(n, notification) && isNewerThan(n, notification))));
  if (stale) return false;
  const hasPopulatedCache = existingQueries.some(([queryKey, data]) => acceptsNotification(queryKey) && !!data?.pages?.[0]);
  if (!hasPopulatedCache) {
    // The refetch's count includes this stack; remember it so a later frame
    // does not add it again.
    addsToBadge(notification, existingQueries);
    invalidateNotificationList();
    return true;
  }

  // Decided once, before any list changes, so every list agrees.
  const bump = existingQueries.some(([, data]) => data?.pages?.[0]?.data.some(n => n.id === notification.id)) || !addsToBadge(notification, existingQueries) ? 0 : 1;
  queryClient.setQueriesData<NotificationInfiniteData>({
    queryKey: [...notificationQueryKeys.all, "list"],
    predicate: query => acceptsNotification(query.queryKey)
  }, oldData => {
    if (!oldData?.pages?.[0]) return oldData;
    const firstPage = oldData.pages[0];
    // Skip if notification already exists (e.g. PubSub redelivery)
    if (firstPage.data.some(n => n.id === alert.notification!.id)) {
      return oldData;
    }
    return {
      ...oldData,
      pages: oldData.pages.map((page, index) => {
        if (!page) return page;
        // The server just hid this stack's previous unread row behind the new
        // one, wherever it sat in the list — drop it so the stack shows once.
        const data = page.data.filter(n => !supersededBy(n, notification));
        if (index !== 0) return {
          ...page,
          data
        };
        return {
          ...page,
          unreadCount: page.unreadCount != null ? page.unreadCount + bump : null,
          data: [notification, ...data]
        };
      })
    };
  });
  return true;
};

/**
 * Patch cached bell rows for a clear the server already applied tenant-wide.
 * `serverUnreadCount` sets the badge exactly; without it the badge falls back to
 * subtracting the hits this cache can see.
 */
const markNotificationsReadInCache = (matchesAlert: (alert: NotificationAlertSummary) => boolean, serverUnreadCount?: number | null, typeStackKey?: string) => {
  // The Unread tab is a `{read:false}` query. Flipping a row to read in place
  // there leaves the list showing something the server would no longer return,
  // and it stays until a refetch — so drop the row from those caches instead of
  // marking it. Every other list keeps the row and just flips it.
  const readFilterOf = (queryKey: readonly unknown[]): boolean | null => {
    const filters = queryKey.length >= 3 && typeof queryKey[2] === "object" ? queryKey[2] as {
      read?: boolean | null;
    } : undefined;
    return filters?.read ?? null;
  };

  // A type stack (say, combined lab results) holds other resources' rows behind its
  // head. Clearing the head's resource makes the server promote one of those, which
  // this patch can't see, so such a clear reads the lists back instead.
  let clearedTypeStack = false;
  const patch = (mode: "flip" | "drop") => (oldData: NotificationInfiniteData | undefined) => {
    if (!oldData?.pages) return oldData;
    const isTarget = (n: NotificationCursorPage["data"][number]) => !n.read && !!n.alert && matchesAlert(n.alert);
    for (const page of oldData.pages) {
      for (const n of page?.data ?? []) {
        // The cleared resource can also sit hidden behind another resource's
        // head, where no cached row matches it, so any stack of the type counts.
        const isTypeStack = n.groupKey?.startsWith("type:") && (n.messageCount ?? 1) > 1;
        if (isTypeStack && (isTarget(n) || !n.read && n.groupKey === typeStackKey)) {
          clearedTypeStack = true;
        }
      }
    }
    let clearedUnread = 0;
    const pages = oldData.pages.map(page => {
      if (!page?.data) return page;
      const hits = page.data.filter(isTarget).length;
      if (!hits) return page;
      clearedUnread += hits;
      return {
        ...page,
        data: mode === "drop" ? page.data.filter(n => !isTarget(n)) : page.data.map(n => isTarget(n) ? {
          ...n,
          read: true
        } : n)
      };
    });

    // The server total is authoritative even when this cache saw no hits: the
    // cleared rows can sit entirely on pages that were never loaded, and the
    // badge still has to come down by all of them.
    if (!clearedUnread && serverUnreadCount == null) return oldData;
    return {
      ...oldData,
      pages: pages.map((page, index) =>
      // unreadCount only exists on the first page.
      index === 0 && page && page.unreadCount != null ? {
        ...page,
        unreadCount: serverUnreadCount != null ? Math.max(0, serverUnreadCount) : Math.max(0, page.unreadCount - clearedUnread)
      } : page)
    };
  };
  queryClient.setQueriesData<NotificationInfiniteData>({
    queryKey: [...notificationQueryKeys.all, "list"],
    predicate: query => readFilterOf(query.queryKey) !== false
  }, patch("flip"));
  queryClient.setQueriesData<NotificationInfiniteData>({
    queryKey: [...notificationQueryKeys.all, "list"],
    predicate: query => readFilterOf(query.queryKey) === false
  }, patch("drop"));
  if (clearedTypeStack) {
    void invalidateNotificationStacks();
  }
};

/** `serverUnreadCount` is the mark-read response's `notificationUnreadCount`. */
export const markConversationNotificationsReadInCache = (conversationId: string, serverUnreadCount?: number | null) => markNotificationsReadInCache(alert => alert.type === AlertType.COMMUNICATION_MESSAGE_RECEIVED && alert.communicationConversationId === conversationId, serverUnreadCount);

/**
 * The caller's Whisper rows for one conversation, after reading it cleared them.
 * `serverUnreadCount` is the mark-read response's `notificationUnreadCount`.
 */
export const markTeamConversationNotificationsReadInCache = (conversationId: string, serverUnreadCount?: number | null) => markNotificationsReadInCache(alert => alert.type === AlertType.TEAM_MESSAGE_RECEIVED && teamConversationIdFrom(alert.content) === conversationId, serverUnreadCount);

/** No server count: an order yields at most one row per provider. */
export const markLabOrderNotificationsReadInCache = (labOrderId: string) => markNotificationsReadInCache(alert => alert.labOrderId === labOrderId, undefined, `type:${AlertType.LAB_RESULTS_READY}`);

/**
 * Whether any of these ids is a cached row that belongs to a stack. Reading or
 * archiving one makes the server re-stack its group (a newer head may have taken it
 * over since the panel loaded), which the optimistic patch can't reproduce.
 */
const touchesCachedStack = (notificationIds: string[]) => {
  const ids = new Set(notificationIds);
  return queryClient.getQueriesData<NotificationInfiniteData>({
    queryKey: [...notificationQueryKeys.all, "list"]
  }).some(([, data]) => data?.pages?.some(page => page?.data?.some(n => ids.has(n.id) && !!n.groupKey)));
};

/**
 * Read back the lists and any expanded stacks after the server re-stacked rows. A head
 * can keep its id while its members change, so its cached expansion would go stale.
 */
const invalidateNotificationStacks = () => Promise.all([queryClient.invalidateQueries({
  queryKey: [...notificationQueryKeys.all, "list"]
}), queryClient.invalidateQueries({
  queryKey: [...notificationQueryKeys.all, "combined"]
})]);
const invalidateNotificationPreferences = () => {
  return queryClient.invalidateQueries({
    queryKey: notificationQueryKeys.preferences()
  });
};

// Mutations
export const useMarkNotificationsReadMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp5,
      onMutate: _temp6,
      onError: _temp8,
      onSettled: _temp9
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useMarkNotificationsUnreadMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp10,
      onMutate: _temp14,
      onError: _temp16,
      onSettled: _temp17
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useArchiveNotificationsMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp18,
      onMutate: _temp19,
      onError: _temp21,
      onSettled: _temp22
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useMuteAlertTypeMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: PREFERENCE_WRITE_MUTATION_KEY,
      mutationFn: _temp23,
      onSettled: _temp24
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};

// --- Preference page mutations ---
//
// IMPORTANT: mutationFn must never read from queryClient cache because
// onMutate has already replaced it with optimistic data by the time
// mutationFn runs. All data needed for the API call is passed via variables.

type TogglePreferenceVars = {
  alertType: AlertType;
  method: NotificationMethod;
  /** ID of existing preference to delete, or null to create. */
  existingPreferenceId: string | null;
  /** Whether a created row only reports the selected location. */
  locationFilterEnabled?: boolean;
  /** Pre-optimistic snapshot of all current preferences. */
  currentPreferences: NotificationPreference[];
};

/**
 * Toggle a single preference on/off. Enabling sends the full preference list
 * (server replaces); disabling deletes the single preference.
 */
export const useTogglePreferenceMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: PREFERENCE_WRITE_MUTATION_KEY,
      mutationFn: _temp26,
      onMutate: _temp27,
      onError: _temp28,
      onSettled: _temp29
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
type ToggleLocationFilterVars = {
  enable: boolean;
  /** Only these types' rows change; omitted, every row does. */
  alertTypes?: AlertType[];
  /** Pre-optimistic snapshot of all current preferences. */
  currentPreferences: NotificationPreference[];
};

/**
 * Turn the location filter on or off for every preference, or for the rows of
 * the given alert types.
 */
export const useToggleLocationFilterMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: PREFERENCE_WRITE_MUTATION_KEY,
      mutationFn: _temp30,
      onMutate: _temp31,
      onError: _temp32,
      onSettled: _temp33
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
type TogglePersistentVars = {
  alertType: AlertType;
  persistent: boolean;
  /** Pre-optimistic snapshot of all current preferences. */
  currentPreferences: NotificationPreference[];
};

/**
 * Toggle "keep until dismissed" for one alert type's IN_APP preference. Persistent
 * is a modifier on an existing IN_APP row, so this is a no-op when in-app
 * notifications are off for that type — the caller disables the control instead.
 */
export const useTogglePersistentMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: PREFERENCE_WRITE_MUTATION_KEY,
      mutationFn: _temp34,
      onMutate: _temp35,
      onError: _temp36,
      onSettled: _temp37
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useBulkNotificationActionMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp38,
      onMutate: _temp41,
      onError: _temp43,
      onSettled: _temp44
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
async function _temp() {
  await queryClient.fetchQuery(notificationQueryOptions.preferences());
  const shared = queryClient.getQueryData(notificationQueryKeys.availableAlertTypes());
  if (shared) {
    return shared;
  }
  return new Set((await getNotificationPreferences())?.availableAlertTypes ?? []);
}
async function _temp2(preset) {
  const data = await applyNotificationPreset(preset);
  if (!data) {
    throw new Error("Couldn't apply the notification level");
  }
  return data;
}
function _temp3(data_0) {
  queryClient.setQueryData(notificationQueryKeys.preferences(), data_0.preferences);
}
function _temp4() {
  return Promise.all([invalidateNotificationPreferences(), queryClient.invalidateQueries({
    queryKey: notificationQueryKeys.presets()
  })]);
}
function _temp5(notificationIds) {
  return markNotificationsRead(notificationIds);
}
async function _temp6(notificationIds_0) {
  await queryClient.cancelQueries({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  const touchesStack = touchesCachedStack(notificationIds_0);
  const previousLists = queryClient.getQueriesData({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  const idsSet = new Set(notificationIds_0);
  const changedUnreadIds = new Set();
  for (const [, data] of previousLists) {
    if (!data?.pages) {
      continue;
    }
    for (const page of data.pages) {
      if (!page?.data) {
        continue;
      }
      for (const n of page.data) {
        if (idsSet.has(n.id) && !n.read) {
          changedUnreadIds.add(n.id);
        }
      }
    }
  }
  const actualUnreadCount = changedUnreadIds.size;
  for (const [queryKey] of previousLists) {
    const filters = queryKey[2] as Omit<NotificationFetchParams, "cursor" | "perPage"> | undefined;
    queryClient.setQueryData(queryKey, oldData => {
      if (!oldData?.pages) {
        return oldData;
      }
      return {
        ...oldData,
        pages: oldData.pages.map((page_0, index) => {
          if (!page_0?.data) {
            return page_0;
          }
          return {
            ...page_0,
            unreadCount: index === 0 && page_0.unreadCount !== null ? Math.max(0, page_0.unreadCount - actualUnreadCount) : page_0.unreadCount,
            data: filters?.read === false ? page_0.data.filter(n_0 => !idsSet.has(n_0.id)) : page_0.data.map(n_1 => idsSet.has(n_1.id) ? {
              ...n_1,
              read: true
            } : n_1)
          };
        })
      };
    });
  }
  return {
    previousLists,
    touchesStack
  };
}
function _temp7(t0) {
  const [queryKey_0, data_0] = t0;
  queryClient.setQueryData(queryKey_0, data_0);
}
function _temp8(_error, _variables, context) {
  if (context?.previousLists) {
    context.previousLists.forEach(_temp7);
  }
}
function _temp9(_data, _error_0, _variables_0, context_0) {
  return context_0?.touchesStack ? invalidateNotificationStacks() : undefined;
}
function _temp10(notificationIds) {
  return markNotificationsUnread(notificationIds);
}
function _temp11(n_0) {
  return n_0.id;
}
function _temp12(page_0) {
  return page_0?.data.map(_temp11) ?? [];
}
function _temp13(a, b) {
  return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
}
async function _temp14(notificationIds_0) {
  await queryClient.cancelQueries({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  const previousLists = queryClient.getQueriesData({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  const idsSet = new Set(notificationIds_0);
  const changedRows = new Map();
  for (const [, data] of previousLists) {
    for (const page of data?.pages ?? []) {
      for (const n of page?.data ?? []) {
        if (idsSet.has(n.id) && n.read) {
          changedRows.set(n.id, {
            ...n,
            read: false
          });
        }
      }
    }
  }
  for (const [queryKey] of previousLists) {
    const filters = queryKey[2] as Omit<NotificationFetchParams, "cursor" | "perPage"> | undefined;
    queryClient.setQueryData(queryKey, oldData => {
      if (!oldData?.pages) {
        return oldData;
      }
      const existingIds = new Set(oldData.pages.flatMap(_temp12));
      const missingRows = Array.from(changedRows.values()).filter(n_1 => !existingIds.has(n_1.id) && (!filters?.alertType?.length || filters.alertType.includes(n_1.alert.type)));
      return {
        ...oldData,
        pages: oldData.pages.map((page_1, index) => {
          if (!page_1?.data) {
            return page_1;
          }
          let rows = filters?.read === true ? page_1.data.filter(n_2 => !idsSet.has(n_2.id)) : page_1.data.map(n_3 => idsSet.has(n_3.id) ? {
            ...n_3,
            read: false
          } : n_3);
          if (filters?.read === false && index === 0 && missingRows.length) {
            rows = [...rows, ...missingRows].sort(_temp13);
          }
          return {
            ...page_1,
            unreadCount: index === 0 && page_1.unreadCount !== null ? page_1.unreadCount + changedRows.size : page_1.unreadCount,
            data: rows
          };
        })
      };
    });
  }
  return {
    previousLists
  };
}
function _temp15(t0) {
  const [queryKey_0, data_0] = t0;
  queryClient.setQueryData(queryKey_0, data_0);
}
function _temp16(_error, _variables, context) {
  if (context?.previousLists) {
    context.previousLists.forEach(_temp15);
  }
}
function _temp17() {
  return invalidateNotificationStacks();
}
function _temp18(notificationIds) {
  return archiveNotifications(notificationIds);
}
async function _temp19(notificationIds_0) {
  await queryClient.cancelQueries({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  const touchesStack = touchesCachedStack(notificationIds_0);
  const previousLists = queryClient.getQueriesData({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  const idsSet = new Set(notificationIds_0);
  const unreadIdsBeingArchived = new Set();
  for (const [, data] of previousLists) {
    if (!data?.pages) {
      continue;
    }
    for (const page of data.pages) {
      if (!page?.data) {
        continue;
      }
      for (const n of page.data) {
        if (idsSet.has(n.id) && !n.read) {
          unreadIdsBeingArchived.add(n.id);
        }
      }
    }
  }
  const unreadBeingArchived = unreadIdsBeingArchived.size;
  queryClient.setQueriesData({
    queryKey: [...notificationQueryKeys.all, "list"]
  }, oldData => {
    if (!oldData?.pages) {
      return oldData;
    }
    return {
      ...oldData,
      pages: oldData.pages.map((page_0, index) => {
        if (!page_0?.data) {
          return page_0;
        }
        return {
          ...page_0,
          unreadCount: index === 0 && page_0.unreadCount !== null ? Math.max(0, page_0.unreadCount - unreadBeingArchived) : page_0.unreadCount,
          data: page_0.data.filter(n_0 => !idsSet.has(n_0.id))
        };
      })
    };
  });
  return {
    previousLists,
    touchesStack
  };
}
function _temp20(t0) {
  const [queryKey, data_0] = t0;
  queryClient.setQueryData(queryKey, data_0);
}
function _temp21(_error, _variables, context) {
  if (context?.previousLists) {
    context.previousLists.forEach(_temp20);
  }
}
function _temp22(_data, _error_0, _variables_0, context_0) {
  return context_0?.touchesStack ? invalidateNotificationStacks() : undefined;
}
function _temp23(preferenceId) {
  return deleteNotificationPreference(preferenceId);
}
function _temp24() {
  invalidateNotificationPreferences();
}
function _temp25(p) {
  return {
    alertType: p.alertType,
    communicationMethod: p.communicationMethod,
    locationFilterEnabled: p.locationFilterEnabled,
    persistent: p.persistent
  };
}
async function _temp26(t0) {
  const {
    alertType,
    method,
    existingPreferenceId,
    locationFilterEnabled: t1,
    currentPreferences
  } = t0;
  const locationFilterEnabled = t1 === undefined ? false : t1;
  if (existingPreferenceId) {
    await deleteNotificationPreference(existingPreferenceId);
  } else {
    await createNotificationPreferences({
      preferences: [...currentPreferences.map(_temp25), {
        alertType,
        communicationMethod: method,
        locationFilterEnabled
      }]
    });
  }
}
async function _temp27(t0) {
  const {
    alertType: alertType_0,
    method: method_0,
    existingPreferenceId: existingPreferenceId_0,
    locationFilterEnabled: t1
  } = t0;
  const locationFilterEnabled_0 = t1 === undefined ? false : t1;
  await queryClient.cancelQueries({
    queryKey: notificationQueryKeys.preferences()
  });
  const previous = queryClient.getQueryData(notificationQueryKeys.preferences()) ?? [];
  const optimistic = existingPreferenceId_0 ? previous.filter(p_0 => p_0.id !== existingPreferenceId_0) : [...previous, {
    id: `optimistic-${alertType_0}-${method_0}`,
    alertType: alertType_0,
    communicationMethod: method_0,
    locationFilterEnabled: locationFilterEnabled_0,
    persistent: defaultPersistentForAlertType(alertType_0),
    createdDate: new Date().toISOString(),
    updatedDate: new Date().toISOString()
  } satisfies NotificationPreference];
  queryClient.setQueryData(notificationQueryKeys.preferences(), optimistic);
  return {
    previous
  };
}
function _temp28(_err, _vars, context) {
  if (context?.previous) {
    queryClient.setQueryData(notificationQueryKeys.preferences(), context.previous);
  }
}
function _temp29() {
  return invalidateNotificationPreferences();
}
async function _temp30(t0) {
  const {
    enable,
    alertTypes,
    currentPreferences
  } = t0;
  await createNotificationPreferences({
    preferences: currentPreferences.map(p => ({
      alertType: p.alertType,
      communicationMethod: p.communicationMethod,
      locationFilterEnabled: !alertTypes || alertTypes.includes(p.alertType) ? enable : p.locationFilterEnabled,
      persistent: p.persistent
    }))
  });
}
async function _temp31(t0) {
  const {
    enable: enable_0,
    alertTypes: alertTypes_0
  } = t0;
  await queryClient.cancelQueries({
    queryKey: notificationQueryKeys.preferences()
  });
  const previous = queryClient.getQueryData(notificationQueryKeys.preferences()) ?? [];
  const optimistic = previous.map(p_0 => !alertTypes_0 || alertTypes_0.includes(p_0.alertType) ? {
    ...p_0,
    locationFilterEnabled: enable_0
  } : p_0);
  queryClient.setQueryData(notificationQueryKeys.preferences(), optimistic);
  return {
    previous
  };
}
function _temp32(_err, _vars, context) {
  if (context?.previous) {
    queryClient.setQueryData(notificationQueryKeys.preferences(), context.previous);
  }
}
function _temp33() {
  return invalidateNotificationPreferences();
}
async function _temp34(t0) {
  const {
    alertType,
    persistent,
    currentPreferences
  } = t0;
  await createNotificationPreferences({
    preferences: currentPreferences.map(p => ({
      alertType: p.alertType,
      communicationMethod: p.communicationMethod,
      locationFilterEnabled: p.locationFilterEnabled,
      persistent: p.alertType === alertType && p.communicationMethod === NotificationMethod.IN_APP ? persistent : p.persistent
    }))
  });
}
async function _temp35(t0) {
  const {
    alertType: alertType_0,
    persistent: persistent_0
  } = t0;
  await queryClient.cancelQueries({
    queryKey: notificationQueryKeys.preferences()
  });
  const previous = queryClient.getQueryData(notificationQueryKeys.preferences()) ?? [];
  const optimistic = previous.map(p_0 => p_0.alertType === alertType_0 && p_0.communicationMethod === NotificationMethod.IN_APP ? {
    ...p_0,
    persistent: persistent_0
  } : p_0);
  queryClient.setQueryData(notificationQueryKeys.preferences(), optimistic);
  return {
    previous
  };
}
function _temp36(_err, _vars, context) {
  if (context?.previous) {
    queryClient.setQueryData(notificationQueryKeys.preferences(), context.previous);
  }
}
function _temp37() {
  return invalidateNotificationPreferences();
}
function _temp38(action) {
  return bulkNotificationAction(action);
}
function _temp39(n) {
  return {
    ...n,
    read: true
  };
}
function _temp40(n_0) {
  return !n_0.read;
}
async function _temp41(action_0) {
  await queryClient.cancelQueries({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  const previousLists = queryClient.getQueriesData({
    queryKey: [...notificationQueryKeys.all, "list"]
  });
  for (const [queryKey] of previousLists) {
    const filters = queryKey[2] as Omit<NotificationFetchParams, "cursor" | "perPage"> | undefined;
    queryClient.setQueryData(queryKey, oldData => {
      if (!oldData?.pages) {
        return oldData;
      }
      return {
        ...oldData,
        pages: oldData.pages.map(page => {
          if (!page?.data) {
            return page;
          }
          if (action_0 === "mark_all_read") {
            return {
              ...page,
              unreadCount: 0,
              data: filters?.read === false ? [] : page.data.map(_temp39),
              nextCursor: filters?.read === false ? null : page.nextCursor
            };
          } else {
            if (action_0 === "archive_all") {
              return {
                ...page,
                data: [],
                unreadCount: 0,
                nextCursor: null
              };
            } else {
              if (action_0 === "archive_read") {
                const unreadNotifications = page.data.filter(_temp40);
                return {
                  ...page,
                  data: unreadNotifications
                };
              }
            }
          }
          return page;
        })
      };
    });
  }
  return {
    previousLists
  };
}
function _temp42(t0) {
  const [queryKey_0, data] = t0;
  queryClient.setQueryData(queryKey_0, data);
}
function _temp43(_error, _variables, context) {
  if (context?.previousLists) {
    context.previousLists.forEach(_temp42);
  }
}
function _temp44() {
  invalidateNotificationList();
}